import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes } from 'react';
import { translateEnumValue } from '@/lib/i18n-core';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-0.5 text-xs font-medium leading-5 transition-colors',
  {
    variants: {
      variant: {
        default: 'border-primary/20 bg-primary/10 text-primary',
        secondary: 'border-transparent bg-secondary text-secondary-foreground',
        outline: 'border-border text-foreground',
        success: 'border-success/20 bg-success/10 text-success',
        warning: 'border-warning/25 bg-warning/15 text-warning',
        destructive: 'border-destructive/20 bg-destructive/10 text-destructive',
        info: 'border-info/20 bg-info/10 text-info',
        muted: 'border-transparent bg-muted text-muted-foreground',
      },
    },
    defaultVariants: { variant: 'default' },
  },
);

export type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

export function Badge({
  className,
  variant,
  ...props
}: HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export type StatusTone = 'success' | 'warning' | 'destructive' | 'info' | 'muted' | 'secondary';

const SETTLED = /paid|completed|released|verified|active|given|issued|approved|dispensed|done|vacant|accepted|confirmed|resolved|settled|delivered|received|posted|passed|(?<!un)allocated|in_effect|open$/;
const UNDERWAY = /in_progress|in_consultation|arrived|checked_in|with_doctor|triage|collected|processing|running|dispatched|sent|submitted|assembled|occupied/;
const WAITING = /pending|partially|draft|booked|ordered|requested|due|queued|scheduled|reserved|waiting|on_hold|hold|review|awaiting|planned|cleaning|maintenance|unmatched/;
const FAILED = /cancelled|canceled|failed|rejected|no_show|missed|refunded|expired|blocked|absconded|lama|void|overdue|error|declined|denied|lost|deceased|needs_review/;
const RETIRED = /archived|inactive|retired|closed|disabled|discharged|returned|superseded|skipped/;

export function statusTone(status: string): StatusTone {
  const value = status.toLowerCase();
  if (FAILED.test(value)) return 'destructive';
  if (RETIRED.test(value)) return 'muted';
  if (SETTLED.test(value)) return 'success';
  if (UNDERWAY.test(value)) return 'info';
  if (WAITING.test(value)) return 'warning';
  return 'secondary';
}

export function sentenceCase(value: string): string {
  const spaced = value.replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!spaced) return '';
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase();
}

export function statusLabel(status: string, group?: string): string {
  const english = sentenceCase(status);
  return translateEnumValue(status, english, group) ?? english;
}

const DOT_TONE: Record<StatusTone, string> = {
  success: 'bg-success',
  warning: 'bg-warning',
  destructive: 'bg-destructive',
  info: 'bg-info',
  muted: 'bg-muted-foreground/60',
  secondary: 'bg-secondary-foreground/50',
};

export function StatusBadge({
  status,
  label,
  tone,
  group,
  className,
}: {
  status: string;
  label?: string;
  tone?: StatusTone;
  group?: string;
  className?: string;
}) {
  const resolved = tone ?? statusTone(status);
  return (
    <Badge variant={resolved} className={className}>
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOT_TONE[resolved])} aria-hidden />
      {label ?? statusLabel(status, group)}
    </Badge>
  );
}
